import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ApiClient, ApiError } from "./api";
import type {
  Admin,
  AdminSnapshot,
  Campaign,
  Cart,
  CatalogItem,
  Customer,
  NetworkSnapshot,
  Order,
  PublicSiteBootstrap,
  Surface,
} from "./types";

const DEFAULT_COLORS = {
  primary: "#17324F",
  accent: "#D98B32",
  background: "#F4F7FA",
  surface: "#FFFFFF",
  text: "#172235",
  muted: "#5F6D7E",
};

const DEFAULT_BOOTSTRAP: PublicSiteBootstrap = {
  implementation: { displayName: "Network Commerce" },
  brand: { name: "Network Commerce", colors: DEFAULT_COLORS, fonts: { body: "system-ui", heading: "system-ui" } },
  surfaces: { store: true, customer: true, marketing: true, network: true, admin: true },
  demo: { mode: "DEMO", sandboxPayments: true, realPayments: false, oauth: false, socialPublishing: false, withdrawals: false },
};

type NavigationSurface = Exclude<Surface, "landing" | "clone">;
const SURFACE_META: Readonly<Record<NavigationSurface, { readonly label: string; readonly description: string }>> = {
  store: { label: "Storefront", description: "Catálogo, carrito y checkout sandbox" },
  customer: { label: "Mi Panel", description: "Cuenta customer y pedidos" },
  marketing: { label: "Marketing", description: "Organic First y sharing manual" },
  network: { label: "Network / Wallet", description: "Red vinculada y saldos derivados" },
  admin: { label: "Back Office", description: "Operación protegida de la implementación" },
};

function surfaceFromPath(pathname: string): Surface {
  const route = pathname.replace(/\/$/, "") || "/";
  if (/^\/clone\/[a-f0-9]{32}$/.test(route)) return "clone";
  if (route === "/store") return "store";
  if (route === "/customer") return "customer";
  if (route === "/marketing") return "marketing";
  if (route === "/network") return "network";
  if (route === "/admin") return "admin";
  return "landing";
}

function go(path: string): void {
  if (window.location.pathname === path) return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function formatMoney(cents: number, currency = "MXN"): string {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency }).format(cents / 100);
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "No se pudo completar la operación.";
}

function readCartId(): string | null {
  try {
    return window.localStorage.getItem("ncp_demo_cart");
  } catch {
    return null;
  }
}

function writeCartId(value: string | null): void {
  try {
    if (value) window.localStorage.setItem("ncp_demo_cart", value);
    else window.localStorage.removeItem("ncp_demo_cart");
  } catch {
    // El carrito también funciona sin persistencia local; el servidor sigue siendo la fuente de verdad.
  }
}

function readTrackingCode(): string | null {
  try {
    const query = new URLSearchParams(window.location.search).get("trackingCode");
    if (query) window.sessionStorage.setItem("ncp_demo_tracking", query);
    return query ?? window.sessionStorage.getItem("ncp_demo_tracking");
  } catch {
    return null;
  }
}

function writeTrackingCode(value: string | null): void {
  try {
    if (value) window.sessionStorage.setItem("ncp_demo_tracking", value);
    else window.sessionStorage.removeItem("ncp_demo_tracking");
  } catch {
    // La atribución Marketing es opcional; el checkout sigue siendo válido sin ella.
  }
}

function SurfaceHeader({ eyebrow, title, children }: { readonly eyebrow: string; readonly title: string; readonly children?: ReactNode }) {
  return (
    <div className="surface-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
      </div>
      {children ? <div className="surface-heading__aside">{children}</div> : null}
    </div>
  );
}

function Panel({ title, children, tone = "default" }: { readonly title?: string; readonly children: ReactNode; readonly tone?: "default" | "accent" | "quiet" }) {
  return <section className={`panel panel--${tone}`}>{title ? <h2>{title}</h2> : null}{children}</section>;
}

function Notice({ children, kind = "info" }: { readonly children: ReactNode; readonly kind?: "info" | "success" | "error" }) {
  return <div className={`notice notice--${kind}`} role={kind === "error" ? "alert" : "status"}>{children}</div>;
}

function Button({ children, variant = "primary", disabled = false, type = "button", onClick }: { readonly children: ReactNode; readonly variant?: "primary" | "secondary" | "accent"; readonly disabled?: boolean; readonly type?: "button" | "submit"; readonly onClick?: () => void }) {
  return <button className={`button button--${variant}`} disabled={disabled} type={type} onClick={onClick}>{children}</button>;
}

function Field({ label, name, type = "text", value, onChange, required = true, placeholder }: { readonly label: string; readonly name: string; readonly type?: string; readonly value: string; readonly onChange: (value: string) => void; readonly required?: boolean; readonly placeholder?: string }) {
  return <label className="field"><span>{label}</span><input name={name} type={type} value={value} onChange={(event) => onChange(event.target.value)} required={required} placeholder={placeholder} /></label>;
}

function LoginCard({ authority, api, onCustomer, onAdmin }: { readonly authority: "customer" | "admin"; readonly api: ApiClient; readonly onCustomer?: (customer: Customer) => void; readonly onAdmin?: (admin: Admin) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [registerMode, setRegisterMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (authority === "customer") {
        if (registerMode) await api.registerCustomer(email, password);
        const customer = await api.loginCustomer(email, password);
        onCustomer?.(customer);
      } else {
        const admin = await api.loginAdmin(email, password);
        onAdmin?.(admin);
      }
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={authority === "customer" ? "Acceso customer" : "Acceso administrativo"} tone="quiet">
      <form className="form-stack" onSubmit={submit}>
        <Field label="Correo" name="email" type="email" value={email} onChange={setEmail} placeholder="nombre@ejemplo.test" />
        <Field label="Contraseña" name="password" type="password" value={password} onChange={setPassword} />
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
        <div className="button-row">
          <Button type="submit" disabled={busy}>{busy ? "Validando…" : authority === "customer" && registerMode ? "Crear y entrar" : "Iniciar sesión"}</Button>
          {authority === "customer" ? <Button variant="secondary" disabled={busy} onClick={() => { setRegisterMode((current) => !current); setError(""); }}>{registerMode ? "Ya tengo cuenta" : "Crear cuenta de piloto"}</Button> : null}
        </div>
      </form>
    </Panel>
  );
}

function AccountSummary({ customer, onLogout, api }: { readonly customer: Customer; readonly onLogout: () => Promise<void>; readonly api: ApiClient }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function logout(): Promise<void> {
    setBusy(true);
    try { await api.logoutCustomer(); await onLogout(); } catch (cause) { setError(errorMessage(cause)); } finally { setBusy(false); }
  }
  return <div className="session-summary"><span className="status-dot" /> <span>Sesión customer: <strong>{customer.email}</strong></span><Button variant="secondary" disabled={busy} onClick={logout}>{busy ? "Saliendo…" : "Cerrar sesión"}</Button>{error ? <span className="inline-error">{error}</span> : null}</div>;
}

function AppShell({ api, bootstrap, route, customer, admin, onCustomerLogout, onAdminLogout, children }: { readonly api: ApiClient; readonly bootstrap: PublicSiteBootstrap; readonly route: Surface; readonly customer: Customer | null; readonly admin: Admin | null; readonly onCustomerLogout: () => Promise<void>; readonly onAdminLogout: () => Promise<void>; readonly children: ReactNode }) {
  const visibleSurfaces = (Object.keys(SURFACE_META) as NavigationSurface[]).filter((surface) => bootstrap.surfaces[surface]);
  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand-lockup" href="/" aria-label="Inicio">
          <span className="brand-mark" aria-hidden="true">N</span>
          <span><strong>{bootstrap.brand.name || bootstrap.implementation.displayName}</strong><small>Network Commerce</small></span>
        </a>
        <nav className="primary-nav" aria-label="Superficies principales">
          {visibleSurfaces.map((surface) => <a key={surface} href={`/${surface}`} className={route === surface ? "is-active" : ""} aria-current={route === surface ? "page" : undefined}>{SURFACE_META[surface].label}</a>)}
        </nav>
        <div className="topbar__status"><span className="demo-badge">PILOTO</span><span className="implementation-chip">{bootstrap.implementation.displayName}</span></div>
      </header>
      <main className="page-frame">{children}</main>
      <footer className="footer"><span>Foundation F4-A · {bootstrap.implementation.displayName}</span><span>Piloto de acceso controlado · no compra real · no retiros · no publicación externa</span></footer>
      {customer && route !== "customer" && route !== "admin" ? <div className="floating-session"><AccountSummary customer={customer} onLogout={onCustomerLogout} api={api} /></div> : null}
      {admin && route === "admin" ? <div className="floating-admin">Admin activo: {admin.email} <button onClick={() => { void onAdminLogout(); }}>Salir</button></div> : null}
      <span className="sr-only" id="session-authority">Customer y Admin usan autoridades separadas.</span>
    </div>
  );
}

function Landing({ bootstrap }: { readonly bootstrap: PublicSiteBootstrap }) {
  return <>
    <section className="hero hero--landing">
      <div className="hero__copy"><p className="eyebrow">{bootstrap.demo.mode} · IMPLEMENTATION-AWARE</p><h1>Una plataforma, superficies claras.</h1><p>Explora el catálogo, opera tu cuenta, prepara Marketing Organic First y consulta la red vinculada sin confundir Customer, Network Participant ni Admin.</p><div className="button-row"><a className="button button--accent" href="/store">Explorar Storefront</a><a className="button button--secondary" href="/customer">Entrar a Mi Panel</a></div></div>
      <div className="hero__signal" aria-label="Estado de la implementación"><span className="signal-orbit" /><span className="signal-label">{bootstrap.implementation.displayName}<small>Brand Theme activo</small></span></div>
    </section>
    <div className="surface-grid surface-grid--landing">{(Object.keys(SURFACE_META) as NavigationSurface[]).map((surface) => <a className="surface-tile" href={`/${surface}`} key={surface}><span className="tile-index">0{Object.keys(SURFACE_META).indexOf(surface) + 1}</span><h2>{SURFACE_META[surface].label}</h2><p>{SURFACE_META[surface].description}</p><span className="tile-link">Abrir superficie →</span></a>)}</div>
    <Notice>Esta implementación está en piloto de acceso controlado y no está disponible para compra real. El checkout usa sandbox, la wallet es derivada del ledger y la publicación social, OAuth y retiros no están habilitados.</Notice>
  </>;
}

function Storefront({ api, customer }: { readonly api: ApiClient; readonly customer: Customer | null }) {
  const [catalog, setCatalog] = useState<readonly CatalogItem[]>([]);
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyOffer, setBusyOffer] = useState<string | null>(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [message, setMessage] = useState<{ readonly text: string; readonly kind: "info" | "success" | "error" }>({ text: "Cargando catálogo…", kind: "info" });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.getCatalog().then((items) => { if (alive) { setCatalog(items); setMessage({ text: "Precios resueltos por el servidor.", kind: "info" }); } }).catch((cause: unknown) => { if (alive) setMessage({ text: errorMessage(cause), kind: "error" }); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api]);

  useEffect(() => {
    let alive = true;
    const savedCartId = readCartId();
    if (!customer || !savedCartId) { setCart(null); return () => { alive = false; }; }
    api.getCart(savedCartId).then((value) => { if (alive) setCart(value); }).catch(() => { writeCartId(null); if (alive) setCart(null); });
    return () => { alive = false; };
  }, [api, customer]);

  async function add(item: CatalogItem): Promise<void> {
    if (!customer) { setMessage({ text: "Inicia sesión en Mi Panel para abrir un carrito customer.", kind: "info" }); go("/customer"); return; }
    setBusyOffer(item.offer_ref);
    try {
      const current = cart ?? await api.createCart();
      if (!cart) writeCartId(current.id);
      const updated = await api.addToCart(current.id, item.offer_ref);
      setCart(updated);
      setMessage({ text: `${item.product_name} fue agregado al carrito.`, kind: "success" });
    } catch (cause) { setMessage({ text: errorMessage(cause), kind: "error" }); }
    finally { setBusyOffer(null); }
  }

  async function checkout(): Promise<void> {
    if (!cart || cart.items.length === 0) return;
    setCheckoutBusy(true);
    setMessage({ text: "Creando pedido y pago sandbox…", kind: "info" });
    try {
      const result = await api.checkout(cart.id, readTrackingCode());
      const payment = await api.createPayment(result.order.id);
      const confirmation = await api.confirmPayment(payment.intent.id);
      setMessage({ text: `Pedido ${confirmation.order.id} confirmado en sandbox.`, kind: "success" });
      setCart(null);
      writeCartId(null);
    } catch (cause) { setMessage({ text: errorMessage(cause), kind: "error" }); }
    finally { setCheckoutBusy(false); }
  }

  return <>
    <SurfaceHeader eyebrow="01 · Storefront" title="Catálogo con precio de servidor"><span className="surface-note">{customer ? `Customer activo: ${customer.email}` : "Consulta pública · checkout requiere Customer"}</span></SurfaceHeader>
    <Notice kind={message.kind}>{message.text}</Notice>
    <div className="store-layout">
      <Panel title="Oferta vigente"><div className="product-grid">{loading ? <EmptyState text="Cargando catálogo…" /> : catalog.length === 0 ? <EmptyState text="No hay productos activos en esta implementación." /> : catalog.map((item) => <article className="product-card" key={item.offer_ref}><div className="product-card__top"><span className="tag">Disponible</span><span className="sku">{item.sku}</span></div><h2>{item.product_name}</h2><p className="muted">{item.variant_name} · {item.offer_name}</p><p>{item.description || "Oferta DEMO de la implementación."}</p><div className="product-card__bottom"><strong>{formatMoney(item.price_cents, item.currency)}</strong><Button variant="accent" disabled={busyOffer === item.offer_ref} onClick={() => { void add(item); }}>{busyOffer === item.offer_ref ? "Agregando…" : "Agregar"}</Button></div></article>)}</div></Panel>
      <Panel title="Tu carrito" tone="accent"><CartSummary cart={cart} checkoutBusy={checkoutBusy} onCheckout={() => { void checkout(); }} onClear={() => { setCart(null); writeCartId(null); }} /></Panel>
    </div>
  </>;
}

function CloneVisitSurface({ api }: { readonly api: ApiClient }) {
  const [state, setState] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("Resolviendo enlace Clone en el servidor…");

  useEffect(() => {
    const match = window.location.pathname.match(/^\/clone\/([a-f0-9]{32})$/);
    if (!match) { setState("error"); setMessage("El enlace Clone no tiene un formato válido."); return; }
    const trackingCode = new URLSearchParams(window.location.search).get("trackingCode");
    if (trackingCode) writeTrackingCode(trackingCode);
    api.visitClone(match[1]).then(() => {
      setState("success");
      setMessage("Visita Clone aceptada: la sesión quedó registrada server-side y el contexto se resolverá al checkout.");
      window.setTimeout(() => go(`/store${trackingCode ? `?trackingCode=${encodeURIComponent(trackingCode)}` : ""}`), 250);
    }).catch((cause: unknown) => { setState("error"); setMessage(errorMessage(cause)); });
  }, [api]);

  return <><SurfaceHeader eyebrow="Clone Attribution" title="Enlace Clone protegido"><span className="surface-note">Sólo identificadores opacos · sin JSON de atribución</span></SurfaceHeader><Notice kind={state === "error" ? "error" : state === "success" ? "success" : "info"}>{message}</Notice>{state === "error" ? <Panel tone="quiet"><div className="empty-state"><h2>No se pudo aceptar el enlace</h2><a className="button button--primary" href="/store">Ir al Storefront</a></div></Panel> : null}</>;
}

function CartSummary({ cart, checkoutBusy, onCheckout, onClear }: { readonly cart: Cart | null; readonly checkoutBusy: boolean; readonly onCheckout: () => void; readonly onClear: () => void }) {
  if (!cart || cart.items.length === 0) return <div className="empty-state"><span className="empty-icon">□</span><p>Tu carrito está vacío.</p><small>Agrega una oferta para comenzar el recorrido sandbox.</small></div>;
  return <div className="cart-summary"><div className="cart-lines">{cart.items.map((item) => <div className="cart-line" key={item.offerRef}><span><strong>{item.offerRef}</strong><small>{item.quantity} × {formatMoney(item.priceCents, item.currency)}</small></span><strong>{formatMoney(item.priceCents * item.quantity, item.currency)}</strong></div>)}</div><div className="cart-total"><span>Total server-priced</span><strong>{formatMoney(cart.subtotalCents, cart.currency)}</strong></div><div className="button-row"><Button variant="accent" disabled={checkoutBusy} onClick={onCheckout}>{checkoutBusy ? "Procesando…" : "Checkout sandbox"}</Button><Button variant="secondary" disabled={checkoutBusy} onClick={onClear}>Limpiar</Button></div><small className="muted">El cobro real no está habilitado; el pago se confirma mediante el provider sandbox.</small></div>;
}

function CustomerSurface({ api, customer, onCustomer, onLogout }: { readonly api: ApiClient; readonly customer: Customer | null; readonly onCustomer: (customer: Customer) => void; readonly onLogout: () => Promise<void> }) {
  const [orders, setOrders] = useState<readonly Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!customer) { setOrders([]); return; }
    let alive = true;
    setLoading(true);
    api.getOrders().then((value) => { if (alive) setOrders(value); }).catch((cause: unknown) => { if (alive) setError(errorMessage(cause)); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, customer]);

  return <>
    <SurfaceHeader eyebrow="02 · Customer" title="Mi Panel"><span className="surface-note">Customer no implica Network Participant.</span></SurfaceHeader>
    {!customer ? <div className="auth-layout"><div className="auth-intro"><p className="eyebrow">Autoridad customer</p><h2>Tu cuenta, tus pedidos.</h2><p>La sesión usa cookie HttpOnly, expiración, revocación, CSRF y rate limiting del backend. El navegador no almacena tokens sensibles.</p><a href="/store" className="text-link">Volver al Storefront →</a></div><LoginCard authority="customer" api={api} onCustomer={onCustomer} /></div> : <div className="content-grid"><Panel title="Cuenta activa" tone="accent"><AccountSummary customer={customer} api={api} onLogout={onLogout} /><p className="muted">Esta cuenta puede comprar y usar el Workspace. La relación con Network se muestra sólo si existe.</p></Panel><Panel title="Pedidos"><div className="order-list">{loading ? <EmptyState text="Cargando pedidos…" /> : error ? <Notice kind="error">{error}</Notice> : orders.length === 0 ? <EmptyState text="Todavía no hay pedidos." /> : orders.map((order) => <div className="order-row" key={order.id}><span><strong>{order.id}</strong><small>{order.status}</small></span><strong>{formatMoney(order.totalCents, order.currency)}</strong></div>)}</div></Panel></div>}
  </>;
}

function MarketingSurface({ api, customer }: { readonly api: ApiClient; readonly customer: Customer | null }) {
  const [campaigns, setCampaigns] = useState<readonly Campaign[]>([]);
  const [name, setName] = useState("Campaña de bienvenida DEMO");
  const [objective, setObjective] = useState("Presentar el catálogo DEMO");
  const [headline, setHeadline] = useState("Conoce una opción DEMO");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function load(): Promise<void> {
    if (!customer) return;
    try { setCampaigns(await api.getCampaigns()); } catch (cause) { setError(errorMessage(cause)); }
  }
  useEffect(() => { void load(); }, [customer]);

  async function create(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!customer) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const catalog = await api.getCatalog();
      const item = catalog[0];
      if (!item) throw new Error("La implementación no tiene una oferta de catálogo activa.");
      const campaign = await api.createCampaign({ name, objective });
      const promotion = await api.createPromotion({ campaignId: campaign.id, name: `Promoción ${name}`, assetRef: item.offer_ref });
      const creative = await api.createCreative({ promotionId: promotion.id, headline });
      await api.activateCampaign(campaign.id);
      await api.activatePromotion(promotion.id);
      const link = await api.createTrackingLink({ campaignId: campaign.id, promotionId: promotion.id, creativeId: creative.id });
      setMessage(`Enlace creado: /v1/marketing/links/${link.code}. Compártelo manualmente en DEMO.`);
      await load();
    } catch (cause) { setError(errorMessage(cause)); }
    finally { setBusy(false); }
  }

  return <>
    <SurfaceHeader eyebrow="03 · Marketing Workspace" title="Organic First, con sharing manual"><span className="surface-note">Sin OAuth · sin publicación automática · first-party</span></SurfaceHeader>
    {!customer ? <Panel tone="quiet"><div className="empty-state"><span className="empty-icon">↗</span><h2>Inicia sesión para abrir Workspace</h2><p>Marketing pertenece a la autoridad Customer y conserva sus eventos en la implementación confiable.</p><a className="button button--primary" href="/customer">Ir a Mi Panel</a></div></Panel> : <div className="content-grid"><Panel title="Crear campaña DEMO" tone="accent"><form className="form-stack" onSubmit={create}><Field label="Nombre de campaña" name="campaignName" value={name} onChange={setName} /><Field label="Objetivo" name="objective" value={objective} onChange={setObjective} /><Field label="Titular del material" name="headline" value={headline} onChange={setHeadline} /><p className="helper">Se crea una promoción, un creativo y un tracking link first-party para compartir manualmente.</p>{error ? <Notice kind="error">{error}</Notice> : null}{message ? <Notice kind="success">{message}</Notice> : null}<Button type="submit" disabled={busy}>{busy ? "Preparando…" : "Crear material DEMO"}</Button></form></Panel><Panel title="Tus campañas"><div className="campaign-list">{campaigns.length === 0 ? <EmptyState text="Aún no tienes campañas." /> : campaigns.map((campaign) => <div className="campaign-row" key={campaign.id}><span><strong>{campaign.name}</strong><small>{campaign.objective}</small></span><span className="tag">{campaign.status}</span></div>)}</div></Panel></div>}
  </>;
}

function NetworkSurface({ api, customer }: { readonly api: ApiClient; readonly customer: Customer | null }) {
  const [snapshot, setSnapshot] = useState<NetworkSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!customer) { setSnapshot(null); return; }
    let alive = true;
    setLoading(true); setError("");
    api.getNetwork().then((value) => { if (alive) setSnapshot(value); }).catch((cause: unknown) => { if (alive) setError(errorMessage(cause)); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, customer]);
  return <>
    <SurfaceHeader eyebrow="04 · Network / Wallet" title="Relación vinculada, no asumida"><span className="surface-note">Wallet derivada del ledger · retiros no disponibles</span></SurfaceHeader>
    {!customer ? <Panel tone="quiet"><div className="empty-state"><span className="empty-icon">◎</span><h2>Necesitas una sesión customer</h2><p>La superficie sólo consulta la relación Network del customer autenticado.</p><a className="button button--primary" href="/customer">Ir a Mi Panel</a></div></Panel> : loading ? <Notice>Cargando relación Network…</Notice> : error ? <Notice kind="error">{error}</Notice> : snapshot ? <div className="network-grid"><Panel title="Vínculo"><div className="metric-hero"><span className="metric-label">Estado del vínculo</span><strong>{snapshot.participant ? "Network Participant vinculado" : "Sin Network Participant"}</strong><p>{snapshot.participant ? `ID ${snapshot.participant.id} · kind ${snapshot.participant.kind}` : "Este customer puede existir sin ser Socio o Networker. A8 continúa abierta."}</p></div></Panel><Panel title="Rango y volumen"><div className="metric-list"><div><span>Rango DEMO</span><strong>{snapshot.metrics?.latestRank?.rankKey ?? "Sin evaluación"}</strong></div><div><span>Volúmenes</span><strong>{Object.entries(snapshot.metrics?.volumes ?? {}).map(([key, value]) => `${key}: ${value}`).join(" · ") || "Sin eventos"}</strong></div></div></Panel><Panel title="Wallet derivada" tone="accent"><div className="wallet-list">{snapshot.wallet.length === 0 ? <EmptyState text="Sin saldos derivados todavía." /> : snapshot.wallet.map((entry) => <div className="wallet-row" key={entry.accountType}><span>{entry.accountType}<small>Derivado del ledger · no retirable</small></span><strong>{formatMoney(entry.balanceCents)}</strong></div>)}</div><p className="helper">Los saldos DEMO no representan autorización de pago ni efectivo disponible.</p></Panel></div> : null}
  </>;
}

function AdminSurface({ api, admin, onAdmin, onLogout }: { readonly api: ApiClient; readonly admin: Admin | null; readonly onAdmin: (admin: Admin) => void; readonly onLogout: () => Promise<void> }) {
  const [snapshot, setSnapshot] = useState<AdminSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!admin) { setSnapshot(null); return; }
    let alive = true;
    setLoading(true);
    api.getAdminSnapshot().then((value) => { if (alive) setSnapshot(value); }).catch((cause: unknown) => { if (alive) setError(errorMessage(cause)); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, admin]);
  return <>
    <SurfaceHeader eyebrow="05 · Back Office" title="Operación protegida"><span className="surface-note">Autoridad Admin separada · sin acciones productivas nuevas</span></SurfaceHeader>
    {!admin ? <div className="auth-layout"><div className="auth-intro"><p className="eyebrow">Autoridad administrativa</p><h2>Revisión operativa DEMO.</h2><p>Esta vista sólo consume rutas Admin ya autorizadas. No crea retiros, pagos reales, configuraciones privadas ni funciones destructivas.</p></div><LoginCard authority="admin" api={api} onAdmin={onAdmin} /></div> : loading ? <Notice>Cargando Back Office…</Notice> : error ? <Notice kind="error">{error}</Notice> : snapshot ? <div className="admin-grid"><Panel title="Resumen Marketing"><div className="stat-grid"><Stat label="Campañas" value={String(snapshot.campaigns.length)} /><Stat label="Eventos" value={String(snapshot.metrics.reduce((total, row) => total + row.count, 0))} /><Stat label="Auditoría" value={String(snapshot.events.length)} /></div></Panel><Panel title="Campañas recientes"><div className="campaign-list">{snapshot.campaigns.length === 0 ? <EmptyState text="Sin campañas." /> : snapshot.campaigns.slice(0, 6).map((campaign) => <div className="campaign-row" key={campaign.id}><span><strong>{campaign.name}</strong><small>{campaign.objective}</small></span><span className="tag">{campaign.status}</span></div>)}</div></Panel><Panel title="Sesión"><p>Admin autenticado: <strong>{admin.email}</strong></p><p className="helper">La cookie y el CSRF de Admin están aislados de Customer.</p><Button variant="secondary" onClick={() => { void onLogout(); }}>Cerrar sesión Admin</Button></Panel></div> : null}
  </>;
}

function Stat({ label, value }: { readonly label: string; readonly value: string }) { return <div className="stat"><span>{label}</span><strong>{value}</strong></div>; }
function EmptyState({ text }: { readonly text: string }) { return <div className="empty-state"><span className="empty-icon">·</span><p>{text}</p></div>; }

export function App() {
  const api = useMemo(() => new ApiClient(), []);
  const [route, setRoute] = useState<Surface>(() => surfaceFromPath(window.location.pathname));
  const [bootstrap, setBootstrap] = useState<PublicSiteBootstrap | null>(null);
  const [bootstrapError, setBootstrapError] = useState("");
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [admin, setAdmin] = useState<Admin | null>(null);

  useEffect(() => {
    const onPopState = () => setRoute(surfaceFromPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let alive = true;
    api.getBootstrap().then((value) => { if (alive) setBootstrap(value); }).catch((cause: unknown) => { if (alive) setBootstrapError(errorMessage(cause)); });
    api.getCustomer().then(async (value) => { if (value) await api.refreshCustomerCsrf(); if (alive) setCustomer(value); }).catch(() => { if (alive) setCustomer(null); });
    api.getAdmin().then(async (value) => { if (value) await api.refreshAdminCsrf(); if (alive) setAdmin(value); }).catch(() => { if (alive) setAdmin(null); });
    return () => { alive = false; };
  }, [api]);

  const activeBootstrap = bootstrap ?? DEFAULT_BOOTSTRAP;
  useEffect(() => {
    const root = document.documentElement;
    const colors = { ...DEFAULT_COLORS, ...(bootstrap?.brand.colors ?? {}) };
    for (const [name, value] of Object.entries(colors)) root.style.setProperty(`--color-${name}`, value);
    const fonts = bootstrap?.brand.fonts ?? {};
    if (fonts.body) root.style.setProperty("--font-body", fonts.body);
    if (fonts.heading) root.style.setProperty("--font-heading", fonts.heading);
  }, [bootstrap]);

  function renderSurface(): ReactNode {
    if (route === "landing") return <Landing bootstrap={activeBootstrap} />;
    if (route === "store") return <Storefront api={api} customer={customer} />;
    if (route === "clone") return <CloneVisitSurface api={api} />;
    if (route === "customer") return <CustomerSurface api={api} customer={customer} onCustomer={setCustomer} onLogout={async () => setCustomer(null)} />;
    if (route === "marketing") return <MarketingSurface api={api} customer={customer} />;
    if (route === "network") return <NetworkSurface api={api} customer={customer} />;
    return <AdminSurface api={api} admin={admin} onAdmin={setAdmin} onLogout={async () => { await api.logoutAdmin(); setAdmin(null); }} />;
  }

  return <AppShell api={api} bootstrap={activeBootstrap} route={route} customer={customer} admin={admin} onCustomerLogout={async () => setCustomer(null)} onAdminLogout={async () => { await api.logoutAdmin(); setAdmin(null); }}>{bootstrapError ? <Notice kind="error">La configuración pública no está disponible: {bootstrapError}. Se muestra fallback neutral; ninguna acción se marca como exitosa.</Notice> : null}{renderSurface()}</AppShell>;
}
